<template>
  <section class="impedance-form pb-3">

    <div class="d-flex justify-content-between flex-wrap align-items-center">
      <h6 class="text-evollu-gray-light is-bold py-3 mb-0">
          {{$t('clinical.people.anamnesis.impedance')}}
      </h6>

      <div class="d-flex align-items-center justify-content-end">

        <div v-if="hasUnsavedChanges">
          <small class="text-orange is-bold">
            {{$t('clinical.people.anamnesis.unsaved')}}
          </small>
        </div>

        <div v-else-if="value && value.updated_at !== null && value.updated_at !== undefined">
          <small>
            {{$t('clinical.people.anamnesis.updated_at')}}
          </small>
          <small class="is-bold">
            {{$t('clinical.people.anamnesis.updated_by', {date: getExpressiveDate(value.updated_at), name: value.updated_by ? value.updated_by.name : ''})}}
          </small>
        </div>

        <i
          v-if="collapsable"
          class="clickable p-3"
          :class="{'fa fa-chevron-up': !isCollapsed, 'fa fa-chevron-down': isCollapsed}"
          @click="isCollapsed = !isCollapsed"
        >
        </i>
      </div>

    </div>

    <div v-if="!isCollapsed" class="mt-3">

      <slot name="beforeForm"></slot>

      <form class="evollu-form" v-if="value">

        <!-- Right Ear-->
        <div class="row align-items-center">
          <label class="is-bold col-12 col-sm-5 col-md-4 mb-0 text-sm-right">
            {{$t('clinical.people.anamnesis.right_ear')}}
          </label>

          <div class="col-12 col-sm-7 col-md-8 col-lg-7 col-xl-6 d-flex flex-column flex-sm-row">
            <va-radio-button
              v-for="(option, index) in impedanceOptions"
              v-model="value.right_ear_impedance_result"
              :key="index"
              :option="option.value"
              :label="$t(option.name)"
              class="col-4 col-lg-3 px-0"
              @input="v => updateValue('right_ear_impedance_result', v)"
              :disabled="!editing"
            />
          </div>

          <!--<div class="text-danger text-break col-12 col-sm-7 col-md-8 col-lg-7 col-xl-6 offset-sm-5 offset-md-4" v-if="formErrors.has('right_ear_otoscopy_result_id')">
            <small>
              {{$t(formErrors.get('right_ear_otoscopy_result_id'))}}
            </small>
          </div>-->
        </div>

        <!-- Impedance Right Ear Obs -->
        <div class="row align-items-start">
          <label class="is-bold col-12 col-sm-5 col-md-4 mb-0 text-sm-right">
            {{$t('clinical.people.anamnesis.right_ear_obs')}}
          </label>

          <div class="col-12 col-sm-7 col-md-8 col-lg-7 col-xl-6">
            <va-input
              id="impedance-right-ear-obs"
              type="textarea"
              :min-rows="3"
              :placeholder="$t('clinical.people.anamnesis.right_ear_obs')"
              :value="value.right_ear_obs"
              @input="v => updateValue('right_ear_obs', v)"
              :error="formErrors.has('right_ear_obs')"
              :error-messages="[$t(formErrors.get('right_ear_obs'))]"
              :disabled="!editing"
            />
          </div>
        </div>

        <!-- Impedance Left Ear Result-->
        <div class="row align-items-center">
          <label class="is-bold col-12 col-sm-5 col-md-4 mb-0 text-sm-right">
            {{$t('clinical.people.anamnesis.left_ear')}}
          </label>

          <div class="col-12 col-sm-7 col-md-8 col-lg-7 col-xl-6 d-flex flex-column flex-sm-row">
            <va-radio-button
              v-for="(option, index) in impedanceOptions"
              v-model="value.left_ear_impedance_result"
              :key="index"
              :option="option.value"
              :label="$t(option.name)"
              class="col-4 col-lg-3 px-0"
              @input="v => updateValue('left_ear_impedance_result', v)"
              :disabled="!editing"
            />
          </div>

          <!--<div class="text-danger text-break col-12 col-sm-7 col-md-8 col-lg-7 col-xl-6 offset-sm-5 offset-md-4" v-if="formErrors.has('left_ear_otoscopy_result_id')">
            <small>
              {{$t(formErrors.get('left_ear_otoscopy_result_id'))}}
            </small>
          </div>-->
        </div>

        <!-- Impedance Left Ear Obs -->
        <div class="row align-items-start">
          <label class="is-bold col-12 col-sm-5 col-md-4 mb-0 text-sm-right">
            {{$t('clinical.people.anamnesis.left_ear_obs')}}
          </label>

          <div class="col-12 col-sm-7 col-md-8 col-lg-7 col-xl-6">
            <va-input
              id="impedance-left-ear-obs"
              type="textarea"
              :min-rows="3"
              :placeholder="$t('clinical.people.anamnesis.left_ear_obs')"
              :value="value.left_ear_obs"
              @input="v => updateValue('left_ear_obs', v)"
              :error="formErrors.has('left_ear_obs')"
              :error-messages="[$t(formErrors.get('left_ear_obs'))]"
              :disabled="!editing"
            />
          </div>
        </div>


        <!-- Ipsilateral reflexes Result-->
        <div class="row align-items-center">
          <label class="is-bold col-12 col-sm-5 col-md-4 mb-0 text-sm-right">
            {{$t('clinical.people.anamnesis.ipsilateral_reflexes')}}
          </label>

          <div class="col-12 col-sm-7 col-md-8 col-lg-7 col-xl-6 d-flex flex-column flex-sm-row">
            <va-radio-button
              v-for="(option, index) in booleanOptions"
              v-model="value.ipsilateral_reflexes"
              :key="index"
              :option="option.value"
              :label="$t(option.name)"
              class="col-4 col-lg-3 px-0"
              @input="v => updateValue('ipsilateral_reflexes', v)"
              :disabled="!editing"
            />
          </div>

          <!--<div class="text-danger text-break col-12 col-sm-7 col-md-8 col-lg-7 col-xl-6 offset-sm-5 offset-md-4" v-if="formErrors.has('left_ear_otoscopy_result_id')">
            <small>
              {{$t(formErrors.get('left_ear_otoscopy_result_id'))}}
            </small>
          </div>-->
        </div>

        <!-- Ipsilateral reflexes Obs -->
        <div class="row align-items-start">
          <label class="is-bold col-12 col-sm-5 col-md-4 mb-0 text-sm-right">
            {{$t('clinical.people.anamnesis.ipsilateral_reflexes_obs')}}
          </label>

          <div class="col-12 col-sm-7 col-md-8 col-lg-7 col-xl-6">
            <va-input
              id="impedance-ipsilateral-reflexes-obs"
              type="textarea"
              :min-rows="3"
              :placeholder="$t('clinical.people.anamnesis.ipsilateral_reflexes_obs')"
              :value="value.ipsilateral_reflexes_obs"
              @input="v => updateValue('ipsilateral_reflexes_obs', v)"
              :error="formErrors.has('ipsilateral_reflexes_obs')"
              :error-messages="[$t(formErrors.get('ipsilateral_reflexes_obs'))]"
              :disabled="!editing"
            />
          </div>
        </div>

        <!-- Contralateral reflexes Result-->
        <div class="row align-items-center">
          <label class="is-bold col-12 col-sm-5 col-md-4 mb-0 text-sm-right">
            {{$t('clinical.people.anamnesis.contralateral_reflexes')}}
          </label>

          <div class="col-12 col-sm-7 col-md-8 col-lg-7 col-xl-6 d-flex flex-column flex-sm-row">
            <va-radio-button
              v-for="(option, index) in booleanOptions"
              v-model="value.contralateral_reflexes"
              :key="index"
              :option="option.value"
              :label="$t(option.name)"
              class="col-4 col-lg-3 px-0"
              @input="v => updateValue('colateral_reflexes', v)"
              :disabled="!editing"
            />
          </div>

          <!--<div class="text-danger text-break col-12 col-sm-7 col-md-8 col-lg-7 col-xl-6 offset-sm-5 offset-md-4" v-if="formErrors.has('left_ear_otoscopy_result_id')">
            <small>
              {{$t(formErrors.get('left_ear_otoscopy_result_id'))}}
            </small>
          </div>-->
        </div>

        <!-- Ipsilateral reflexes Obs -->
        <div class="row align-items-start">
          <label class="is-bold col-12 col-sm-5 col-md-4 mb-0 text-sm-right">
            {{$t('clinical.people.anamnesis.contralateral_reflexes_obs')}}
          </label>

          <div class="col-12 col-sm-7 col-md-8 col-lg-7 col-xl-6">
            <va-input
              id="impedance-contralateral-reflexes-obs"
              type="textarea"
              :min-rows="3"
              :placeholder="$t('clinical.people.anamnesis.contralateral_reflexes_obs')"
              :value="value.contralateral_reflexes_obs"
              @input="v => updateValue('contralateral_reflexes_obs', v)"
              :error="formErrors.has('contralateral_reflexes_obs')"
              :error-messages="[$t(formErrors.get('contralateral_reflexes_obs'))]"
              :disabled="!editing"
            />
          </div>
        </div>


        <!-- Obs -->
        <div class="row align-items-start">
          <label class="is-bold col-12 col-sm-5 col-md-4 mb-0 text-sm-right">
            {{$t('clinical.people.anamnesis.tonal_audiometry_obs')}}
          </label>

          <div class="col-12 col-sm-7 col-md-8 col-lg-7 col-xl-6">
            <va-input
              id="impedance-general-obs"
              type="textarea"
              :min-rows="3"
              :placeholder="$t('clinical.people.anamnesis.tonal_audiometry_obs')"
              :value="value.obs"
              @input="v => updateValue('obs', v)"
              :error="formErrors.has('obs')"
              :error-messages="[$t(formErrors.get('obs'))]"
              :disabled="!editing"
            />
          </div>
        </div>

      </form>
      <div v-else class="ml-2">
        <slot name="noData">
          <span>{{$t('clinical.people.no_data')}}</span>
        </slot>
      </div>
    </div>

  </section>
</template>

<script>
import Errors from '@/utils/StandardErrors'
import Multiselect from 'vue-multiselect'
import * as AnamnesisOptions from './AnamnesisOptions'

export default {
  props: {
    value: {
      type: Object,
      required: false
    },
    formErrors: {
      type: Object,
      required: false,
      default: () => {
        return new Errors()
      }
    },
    editing: {
      type: Boolean,
      default: true
    },
    collapsable: {
      type: Boolean,
      default: false
    },
    collapsed: {
      type: Boolean,
      default: false
    }
  },
  components: {
    Multiselect
  },
  data: function () {
    return {
      isCollapsed: this.collapsable && this.collapsed,
      formOptions: AnamnesisOptions,
      hasUnsavedChanges: false,
      booleanOptions: [
        {
          value: true,
          name: "yes"
        },
        {
          value: false,
          name: "no"
        }
      ],
      impedanceOptions: [
        {
          value: "A",
          name: "A"
        },
        {
          value: "B",
          name: "B"
        },
        {
          value: "C",
          name: "C"
        },
      ],
    }
  },
  methods: {
    getHasUnsavedChanges: function () {
      return this.hasUnsavedChanges
    },
    setHasUnsavedChanges: function (value) {
      this.hasUnsavedChanges = value
    },
    getDefaultValue: function () {
      return {
        right_ear_impedance_result: null,
        right_ear_obs: null,
        left_ear_impedance_result: null,
        left_ear_obs: null,
        ipsilateral_reflexes: null,
        ipsilateral_reflexes_obs: null,
        contralateral_reflexes: null,
        contralateral_reflexes_obs: null,
        obs: null
      }
    },
    updateValue: function (field, value) {
      if (this.formErrors.has(field)) {
        this.formErrors.clear(field)
      }
      this.$emit('input', { ...this.value, [field]: value })
      this.$emit('clearErrors', field)

      if (!this.hasUnsavedChanges) {
        this.hasUnsavedChanges = true
      }
    },
    getExpressiveDate: function (value) {
      if (value) {
        if (this.$options.filters.isToday(value)) {
          return this.$t('date_helpers.today')
        } else {
          return this.$options.filters.localTime(value, `DD [${this.$t('date_helpers.day_of_month_separator')}] MMMM [${this.$t('date_helpers.day_of_month_separator')}] YYYY`, '', this.locale)
        }
      }
      return ''
    }
  },
  watch: {
    value: function (value) {
      if ((value === null || value === undefined) && this.editing) {
        this.$emit('input', { ...this.getDefaultValue() })
      }
    },
    collapsed: function () {
      this.isCollapsed = this.collapsable && this.collapsed
    },
    editing: {
      handler: function (editing) {
        if (editing) {
          this.$emit('input', { ...this.getDefaultValue(), ...this.value })
        }
      },
      immediate: true
    }
  }
}
</script>
